/**
 * One recording, from the recorder starting to the diagnostics being read.
 *
 * The span holds what the recording is judged against — the tracks' state at
 * start, the stream the recorder was given, and the audio energy measured while
 * it ran.
 */
import type { LevelWindow } from "@/lib/audio-level-windows";
import {
  createRecordingLevelAccumulator,
  type RecordingLevelAccumulator,
  type RecordingLevelSummary,
} from "@/lib/recording-level-summary";

import type { AudioGraphContextState } from "./use-audio-graph";
import {
  type AudioDeviceSnapshot,
  type MediaTrackSummary,
  summarizeTracks,
  type TrackSummary,
} from "./use-media-stream";

/** Outcome of a single track kind across a completed recording. */
export type TrackOutcome =
  | "present"
  | "missing_at_start"
  | "lost_mid_recording";

/** Video outcome adds `disabled` for a camera the student turned off. */
export type VideoOutcome = TrackOutcome | "disabled";

/** A track's verdict for the recording, beside the state it was judged from. */
export interface TrackDiagnostics<Outcome extends string> {
  outcome: Outcome;
  /** The track's state when the recording stopped. */
  atCapture: MediaTrackSummary;
}

/**
 * What a completed recording carried: one entry per track, and the audio energy
 * measured while it ran.
 */
export interface RecordingDiagnostics {
  camera: TrackDiagnostics<VideoOutcome>;
  /**
   * The microphone track. It has no verdict of its own — it is the evidence
   * that separates a graph fault from a device fault.
   */
  microphone: MediaTrackSummary;
  /** The audio track the file actually contains */
  recordedAudio: TrackDiagnostics<TrackOutcome>;
  /** The microphone the recording started on. */
  audioDevice: AudioDeviceSnapshot;
  /** The audio graph's state when the recording stopped. */
  audioContextState: AudioGraphContextState;
  /**
   * Audio energy across the recording. Null on callers that do not ask for level
   * windows — a track that nobody measures cannot be reported on.
   */
  levelSummary: RecordingLevelSummary | null;
}

/** What the recorder was handed, and the track state it started from. */
export interface RecordingSpanStart {
  /** The stream the recorder was given. */
  recordedStream: MediaStream;
  /** The raw stream the student's devices feed. */
  rawStream: MediaStream | null;
  cameraEnabled: boolean;
  audioDevice: AudioDeviceSnapshot;
  /** `Date.now()` at start; windows that opened earlier are not this span's. */
  startedAtMs: number;
  /** False on callers that do not ask for level windows. */
  measureLevels: boolean;
}

export interface RecordingSpan {
  begin(start: RecordingSpanStart): void;
  addLevelWindow(window: LevelWindow): void;
  /** Closes the level measurement. */
  end(): void;
  /** The stream the recorder was given, or null outside a recording. */
  recordedStream(): MediaStream | null;
  /** The recording's verdict. Ends the span. */
  takeDiagnostics(atCapture: {
    rawTracks: TrackSummary;
    recordedAudio: MediaTrackSummary;
    contextState: AudioGraphContextState;
  }): RecordingDiagnostics;
  /** Ends the span with nothing to report. */
  discard(): void;
}

const NO_AUDIO_DEVICE: AudioDeviceSnapshot = { label: null, settings: null };

// A track is usable at capture if it is still live and not muted.
function usableAtCapture(summary: MediaTrackSummary): boolean {
  return summary.live && summary.muted !== true;
}

function trackOutcome(liveAtStart: boolean, usable: boolean): TrackOutcome {
  if (!liveAtStart) return "missing_at_start";
  if (!usable) return "lost_mid_recording";
  return "present";
}

export function createRecordingSpan(): RecordingSpan {
  let recordedStream: MediaStream | null = null;
  let videoLive = false;
  let recordedAudioLive = false;
  let cameraEnabled = false;
  let audioDevice: AudioDeviceSnapshot = NO_AUDIO_DEVICE;
  let levels: RecordingLevelAccumulator | null = null;
  let levelSummary: RecordingLevelSummary | null = null;

  const reset = () => {
    recordedStream = null;
    levels = null;
    levelSummary = null;
  };

  return {
    begin(start) {
      recordedStream = start.recordedStream;
      videoLive = summarizeTracks(start.rawStream).video.live;
      recordedAudioLive = summarizeTracks(start.recordedStream).audio.live;
      cameraEnabled = start.cameraEnabled;
      audioDevice = start.audioDevice;
      levels = start.measureLevels
        ? createRecordingLevelAccumulator({ startedAtMs: start.startedAtMs })
        : null;
      levelSummary = null;
    },

    addLevelWindow(window) {
      levels?.add(window);
    },

    end() {
      levelSummary = levels?.summarise() ?? null;
      levels = null;
    },

    recordedStream() {
      return recordedStream;
    },

    takeDiagnostics({ rawTracks, recordedAudio, contextState }) {
      const diagnostics: RecordingDiagnostics = {
        recordedAudio: {
          outcome: trackOutcome(
            recordedAudioLive,
            usableAtCapture(recordedAudio)
          ),
          atCapture: recordedAudio,
        },
        microphone: rawTracks.audio,
        camera: {
          // A camera the student never enabled is "disabled", not a failure.
          outcome: cameraEnabled
            ? trackOutcome(videoLive, usableAtCapture(rawTracks.video))
            : "disabled",
          atCapture: rawTracks.video,
        },
        audioDevice,
        audioContextState: contextState,
        levelSummary,
      };

      reset();
      return diagnostics;
    },

    discard: reset,
  };
}
